import { ReactNode, useCallback, useEffect, useState } from "react";
import { DesignContext } from "./DesignContext";
import { useMultiplayer } from "../Multiplayer/useMultiplayer";
import { ROUTES } from "../../constant/common.constant";
import { matchPath, useNavigate } from "react-router-dom";
import { DesignState, Image } from "../../types";
import DesignStateService from "../../service/DesignStateService";
import { AxiosError } from "axios";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import DesignService from "../../service/DesignService";

interface Props {
  children: ReactNode;
}

export const DesignProvider = ({ children }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const path = window.location.pathname;
  const match = matchPath(`${ROUTES.STUDIO}/*` as any, path);
  const designId = match?.params?.designId ?? undefined;
  const [design, setDesign] = useState<DesignState | undefined>(undefined);
  const [isDesignLoading, setIsDesignLoading] = useState(false);
  const { subscribeToDesign, unsubscribeFromDesign, isConnected } =
    useMultiplayer();

  useEffect(() => {
    if (!designId || !isConnected) return;
    subscribeToDesign(designId);

    return () => {
      unsubscribeFromDesign();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [designId, isConnected]);

  const getDesign = useCallback(
    async (options?: { skipLoading?: boolean }) => {
      if (!options?.skipLoading) {
        setIsDesignLoading(true);
      }
      if (!designId) return;
      try {
        const { data: designState } =
          await DesignStateService.getDesignState(designId);

        const { data: res } = await DesignService.getDesign(designId);

        setDesign({
          ...designState,
          ...res.data,
        });
      } catch (error) {
        if (error instanceof AxiosError && error.response?.status === 404) {
          toast.warn(t("design.not_found"));
          navigate(ROUTES.HOME, { replace: true });
        }
      } finally {
        setIsDesignLoading(false);
      }
    },
    [designId, t, navigate]
  );

  const updateDesignImages = useCallback((images: Image[]) => {
    setDesign((prev) => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        images,
      };
    });
  }, []);

  const clearDesign = useCallback(() => {
    setDesign(undefined);
  }, []);

  return (
    <DesignContext.Provider
      value={{
        designId,
        design,
        isDesignLoading,
        refetchDesign: getDesign,
        clearDesign,
        updateDesignImages,
      }}
    >
      {children}
    </DesignContext.Provider>
  );
};
